import { useMemo } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ClipboardList, Check } from "lucide-react";
import { getPolicyDeadlines, markRoomingListSubmitted } from "@/services/orders";
import { useCan } from "@/auth/permissions";

/**
 * HOTELENH ④ · Rooming List 提交提醒（前端实时算 · 无 cron · 车辆证照 60/30 同款）
 *
 * 吃只读端点 GET /api/v1/orders/_policy-deadlines（一个端点喂日历角标 + 本提醒）。
 * 规则：入住日 − rooming_list_days = 提交截止 · 未提交 + 临近/逾期 → 提醒；已提交不显。
 * 首页 OP todo + 订单列表复用同一组件 · 无待办时 render null（不干扰 canvas 基线）。
 */

const WINDOW_DAYS = 7; // 提前 7 天进入提醒窗口
const LOOKBACK_DAYS = 30; // 往回 30 天捞逾期未提交（check-in 仍未过的才提醒）

function toISO(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
function shiftISO(base: string, days: number): string {
  const [y, m, d] = base.split("-").map(Number);
  const dt = new Date(y, m - 1, d + days);
  return toISO(dt);
}
/** toISO − fromISO（天数 · 可负 = 已逾期）*/
function daysBetween(fromISO: string, toISODate: string): number {
  const [ay, am, ad] = fromISO.split("-").map(Number);
  const [by, bm, bd] = toISODate.split("-").map(Number);
  const a = Date.UTC(ay, am - 1, ad);
  const b = Date.UTC(by, bm - 1, bd);
  return Math.round((b - a) / 86400000);
}

interface RoomingRow {
  order_id: string;
  order_no: string;
  hotel_name: string | null;
  room_type_label: string | null;
  check_in_date: string;
  rooming_deadline: string;
  daysLeft: number; // 距提交截止（负=逾期）
}

/** 分级：逾期 danger / ≤2 天 amber / 其余 blue（与车辆证照 tier 同调性）*/
function tier(daysLeft: number): { label: string; badge: string; row: string } {
  if (daysLeft < 0) return { label: `逾期 ${Math.abs(daysLeft)} 天`, badge: "bg-rose-600 text-white", row: "border-rose-200 bg-rose-50" };
  if (daysLeft <= 2) return { label: `${daysLeft} 天后截止`, badge: "bg-amber-500 text-white", row: "border-amber-200 bg-amber-50" };
  return { label: `${daysLeft} 天后截止`, badge: "bg-blue-100 text-blue-700", row: "border-slate-200 bg-white" };
}

export function RoomingListReminder({ variant = "page" }: { variant?: "page" | "home" }) {
  const qc = useQueryClient();
  const today = toISO(new Date());
  const from = shiftISO(today, -LOOKBACK_DAYS);
  const to = shiftISO(today, WINDOW_DAYS);

  const q = useQuery({
    queryKey: ["orders", "rooming-reminder", from, to],
    queryFn: () => getPolicyDeadlines(from, to),
    staleTime: 60_000,
  });

  const items = useMemo<RoomingRow[]>(() => {
    return (q.data ?? [])
      .filter((r) => r.rooming_deadline && !r.rooming_list_submitted_at && r.check_in_date >= today)
      .map((r) => ({
        order_id: r.order_id,
        order_no: r.order_no,
        hotel_name: r.hotel_name,
        room_type_label: r.room_type_label,
        check_in_date: r.check_in_date,
        rooming_deadline: r.rooming_deadline as string,
        daysLeft: daysBetween(today, r.rooming_deadline as string),
      }))
      .filter((r) => r.daysLeft <= WINDOW_DAYS)
      .sort((a, b) => a.daysLeft - b.daysLeft);
  }, [q.data, today]);

  const canMark = useCan("orders.orders.update"); // POST rooming-list-submitted
  const markMut = useMutation({
    mutationFn: (orderId: string) => markRoomingListSubmitted(orderId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["orders", "rooming-reminder"] });
    },
  });

  // 无待办 → 不渲染（首页 canvas 基线不受影响）
  if (q.isLoading || items.length === 0) return null;

  const overdueN = items.filter((r) => r.daysLeft < 0).length;

  return (
    <section className="flex flex-col gap-3 rounded-xl border border-amber-200 bg-amber-50/60 p-4">
      <div className="flex items-center gap-2">
        <ClipboardList size={16} className="text-amber-600" />
        <h2 className="text-[14px] font-bold text-slate-900">
          Rooming List 待提交名单（{items.length}）
        </h2>
        {overdueN > 0 && (
          <span className="rounded-full bg-rose-600 px-2 py-0.5 text-[10px] font-bold text-white">
            {overdueN} 单已逾期
          </span>
        )}
        <span className="ml-auto text-[11px] text-slate-500">提交截止 = 入住日 − 政策天数 · 提交后自动消除</span>
      </div>

      <div className="flex flex-col gap-2">
        {items.slice(0, variant === "home" ? 4 : items.length).map((r) => {
          const t = tier(r.daysLeft);
          return (
            <div
              key={r.order_id}
              className={`flex flex-wrap items-center gap-3 rounded-lg border px-3 py-2 ${t.row}`}
            >
              <Link
                to={`/orders/${r.order_id}`}
                className="w-24 shrink-0 font-mono text-[12px] font-bold text-blue-600 hover:underline"
              >
                {r.order_no}
              </Link>
              <div className="min-w-0 flex-1">
                <div className="truncate text-[12px] font-semibold text-slate-900">
                  {r.hotel_name ?? "—"}
                  {r.room_type_label ? ` · ${r.room_type_label}` : ""}
                </div>
                <div className="truncate text-[10px] text-slate-500">
                  入住 {r.check_in_date} · 名单截止 {r.rooming_deadline}
                </div>
              </div>
              <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold ${t.badge}`}>
                {t.label}
              </span>
              {canMark && <button
                type="button"
                onClick={() => markMut.mutate(r.order_id)}
                disabled={markMut.isPending && markMut.variables === r.order_id}
                title="标记名单已提交给酒店（撤销可在订单详情操作）"
                className="flex h-7 shrink-0 items-center gap-1 rounded-full bg-emerald-600 px-2.5 text-[11px] font-semibold text-white hover:bg-emerald-700 disabled:opacity-40"
              >
                <Check size={12} /> 标记已提交
              </button>}
            </div>
          );
        })}
      </div>

      {variant === "home" && items.length > 4 && (
        <Link to="/orders" className="text-[11px] font-medium text-amber-700 hover:underline">
          还有 {items.length - 4} 单待提交 → 去订单中心
        </Link>
      )}
      {markMut.isError && (
        <p className="text-[11px] text-rose-600">标记失败：{(markMut.error as Error)?.message ?? "请重试"}</p>
      )}
    </section>
  );
}
